import { memo, useMemo } from 'react';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { INotification, NOTIFICATION_TYPE } from '../../interfaces/INotification';
import { ReactLink } from '@dfl/react-security';

interface NotificationUserItemProps {
  notification: INotification;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const NotificationUserItem = memo(({ notification, onDelete }: NotificationUserItemProps) => {
  const { t } = useTranslation('notification');

  // Get the appropriate router based on notification type
  const linkRouter = useMemo(() => {
    switch (notification?.type) {
      case NOTIFICATION_TYPE.SUPPLIER_CREATE:
        return `/inventory/provider/suppliers/${notification?.data?.id || ''}/general`;
      case NOTIFICATION_TYPE.USER_ASSIGNED:
      case NOTIFICATION_TYPE.USER_UNASSIGNED:
        return `/sales/incidences/${notification?.data?._id || ''}/general`;
      default:
        return `/crm/users/${notification?.data?.owner?._id || ''}/general`;
    }
  }, [notification]);

  // Get the appropriate router based on user (executed by)
  const userProfileRouter = useMemo(() => {
    return `/security/system-users/user/${notification?.executedBy?._id || ''}/general`;
  }, [notification]);

  // Get display name based on notification type
  // const displayName = useMemo(() => {
  //   if (notification?.type === 'SUPPLIER_CREATE') {
  //     return notification.data?.name || t('common:unknown');
  //   }
  //   return `${notification?.data?.owner?.firstName || ''} ${notification?.data?.owner?.lastName || ''}`.trim();
  // }, [notification, t]);

  const getInitials = (name?: string, firstName?: string, lastName?: string) => {
    if (name) {
      // Get first letter of first two words
      const words = name.split(' ').filter((word) => word.length > 0);
      if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  const initials = useMemo(() => {
    if (notification?.type === 'SUPPLIER_CREATE') {
      return getInitials(notification.data?.name);
    }
    return getInitials('', notification?.data?.owner?.firstName, notification?.data?.owner?.lastName);
  }, [notification]);

  const relativeTime = formatDistanceToNow(new Date(notification?.createdAt || new Date()), {
    addSuffix: true,
    locale: es,
  });

  const notificationTitle = useMemo(() => {
    const titleKey = `titles.${notification?.type}`;
    const hasTitle = t(titleKey) !== titleKey;
    return hasTitle ? t(titleKey) : null;
  }, [notification?.type, t]);

  const notificationDescription = useMemo(() => {
    switch (notification?.type) {
      case NOTIFICATION_TYPE.SUPPLIER_CREATE:
      case NOTIFICATION_TYPE.MANUFACTURE_CREATE:
      case NOTIFICATION_TYPE.DISTRIBUTION_CENTER_CREATE:
      case NOTIFICATION_TYPE.WAREHOUSE_CREATE:
      case NOTIFICATION_TYPE.CATEGORY_CREATE:
      case NOTIFICATION_TYPE.LOGISTIC_PROVIDER_CREATE:
        return (
          <Typography variant='body2' color='text.secondary'>
            <ReactLink to={linkRouter}>{notification.data?.name || t('common:unknown')}</ReactLink>
            <span> {t('descriptions.SUPPLIER_CREATE')} </span>
            <ReactLink to={userProfileRouter}>{notification.executedBy?.fullName || t('common:someone')}</ReactLink>
          </Typography>
        );
      case NOTIFICATION_TYPE.USER_ASSIGNED:
      case NOTIFICATION_TYPE.USER_UNASSIGNED:
        return (
          <Typography variant='body2' color='text.secondary'>
            <span> {t(`action.${notification?.type}`)} </span>
            <ReactLink to={linkRouter}>{notification.data.code}</ReactLink>
          </Typography>
        );
      case NOTIFICATION_TYPE.RECOVERY_PAID_CANCELLED_ORDER:
        return (
          <Typography variant='body2' color='text.secondary'>
            <span>{t('descriptions.RECOVERY_PAID_CANCELLED_ORDER')} </span>
            <ReactLink to={`/sales/orders/${notification.data.orderId ?? ''}/general`}>
              {notification.data.orderCode ?? ''}
            </ReactLink>
            <span> {t('recoveryPaidCancelledOrder.post')} </span>
            {notification.data.incidence ? (
              <>
                <span>{t('recoveryPaidCancelledOrder.withIncidence')} </span>
                <ReactLink to={`/sales/incidences/${notification.data.incidence._id}/general`}>
                  {notification.data.incidence.code}
                </ReactLink>
              </>
            ) : (
              <span>{t('recoveryPaidCancelledOrder.noIncidence')}</span>
            )}
          </Typography>
        );
      default:
        return (
          <Typography variant='body2' color='text.secondary'>
            <ReactLink to={linkRouter}>{notification?.data?.email}</ReactLink>
            <span> {t(`descriptions.${notification?.type}`)}</span>
          </Typography>
        );
    }
  }, [
    linkRouter,
    notification.data.code,
    notification.data?.email,
    notification.data?.name,
    notification.data?.incidence,
    notification.data?.orderCode,
    notification.data?.orderId,
    notification.executedBy?.fullName,
    notification?.type,
    t,
    userProfileRouter,
  ]);

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        cursor: 'pointer',
        position: 'relative',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      id={notification?._id}
      {...(notification?._id ? { 'data-notification-id': notification._id } : {})}
    >
      <Stack direction='row' spacing={2}>
        <Box position='relative'>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {initials}
          </Avatar>
          {notification?.viewed ? (
            <FiberManualRecordIcon
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: 14,
                color: '#B1ACB0',
                bgcolor: 'white',
                borderRadius: '50%',
              }}
            />
          ) : (
            <FiberManualRecordIcon
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: 14,
                color: 'success.main',
                bgcolor: 'white',
                borderRadius: '50%',
              }}
            />
          )}
        </Box>

        <Box flex={1}>
          <Stack spacing={1}>
            {notificationTitle && (
              <Typography variant='body2' fontWeight={600} color='text.primary'>
                {notificationTitle}
              </Typography>
            )}
            <div className='w-full'>{notificationDescription}</div>

            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='caption' color='text.secondary'>
                {relativeTime}
              </Typography>

              {notification?.type !== 'SUPPLIER_CREATE' && notification?.data?.name && (
                <>
                  <Typography variant='caption' color='text.secondary'>
                    •
                  </Typography>
                  <Typography variant='caption' color='text.secondary' fontWeight={500}>
                    {notification.data.name}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        </Box>

        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(notification._id);
          }}
          sx={{
            alignSelf: 'flex-start',
            mt: 0.5,
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
              bgcolor: 'error.light',
              color: 'white',
            },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    </Box>
  );
});

NotificationUserItem.displayName = 'NotificationUserItem';
